import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, IconButton, Stepper, Step, StepLabel, CircularProgress, Alert,
  Autocomplete, TextField, RadioGroup, FormControlLabel, Radio, Chip, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { waveService } from '../services/waveService';
import { equipmentService } from '../services/equipmentService';
import { screenService } from '../services/screenService';
import { wmsPersonnelPermissionService } from '../services/wmsPersonnelPermissionService';
import { getPersonnel as getPersonnelSql } from '../../../services/migration/personnelDataService';
import { useAuth } from '../../../contexts/AuthContext';

const WMS_WAVE_STATUS_EVENT = 'wms-wave-status-changed';
const WMS_WAVE_STATUS_STORAGE_KEY = 'wms-wave-status-ts';

interface WizardWave {
  id: string;
  waveNumber: string;
  assignedPickerId?: string;
  assignedPersonnelId?: string;
  assignedPickerName?: string;
  cartId?: string;
  cartCode?: string;
}

interface PersonnelOption {
  id: string;
  name: string;
  email?: string;
}

interface Cart {
  id: string;
  cartCode: string;
  cartName: string;
  status: string;
  isActive: boolean;
}

interface WaveSetupWizardProps {
  open: boolean;
  wave: WizardWave | null;
  warehouseId: string;
  initialStep?: number;
  onClose: () => void;
  onRefresh: () => void;
}

const STEPS = ['Toplayıcı', 'Araba', 'Özet & Başlat'];

const isLegacyFallbackStatus = (status?: number) => status === 404 || status === 405;

const getApiErrorMessage = (err: any, fallback: string) => {
  const payload = err?.response?.data;
  const apiMessage = payload?.Message || payload?.message;
  if (apiMessage) return apiMessage as string;
  const status = err?.response?.status;
  if (status) return `${fallback} (HTTP ${status})`;
  return fallback;
};

const WaveSetupWizard: React.FC<WaveSetupWizardProps> = ({
  open, wave, warehouseId, initialStep, onClose, onRefresh,
}) => {
  const { userDetails } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Toplayıcı (picker) adımı
  const [pickerOptions, setPickerOptions] = useState<PersonnelOption[]>([]);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState('');
  const [loadingPickers, setLoadingPickers] = useState(false);
  const [pickerInfo, setPickerInfo] = useState('');

  // Araba (cart) adımı
  const [carts, setCarts] = useState<Cart[]>([]);
  const [selectedCartId, setSelectedCartId] = useState('');
  const [loadingCarts, setLoadingCarts] = useState(false);

  const currentPickerId = wave?.assignedPersonnelId || wave?.assignedPickerId || '';
  const currentCartId = wave?.cartId || '';

  // Sihirbaz açılınca: mevcut atamaları doldur ve ilk eksik adımdan başla.
  useEffect(() => {
    if (!open || !wave) return;
    setSelectedPersonnelId(currentPickerId);
    setSelectedCartId(currentCartId);
    setError('');
    const firstIncomplete = !currentPickerId ? 0 : !currentCartId ? 1 : 2;
    setActiveStep(typeof initialStep === 'number' ? initialStep : firstIncomplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, wave?.id]);

  // Personel listesini yükle (yalnızca Toplama ekranına yetkili olanlar).
  useEffect(() => {
    if (!open) return;
    const loadOptions = async () => {
      setLoadingPickers(true);
      setPickerInfo('');
      try {
        if (!userDetails?.companyId) {
          setPickerOptions([]);
          setPickerInfo('Şirket bilgisi bulunamadı.');
          return;
        }
        const sqlPersonnel = await getPersonnelSql(userDetails.companyId);
        const basePersonnel: PersonnelOption[] = (sqlPersonnel || [])
          .map((raw: any) => ({ id: raw.id, name: raw?.name || 'İsimsiz Personel', email: raw?.email }))
          .filter((p) => !!p.id && !!p.name);

        const screensResp = await screenService.getScreens('picking', warehouseId);
        const screens = screensResp.data?.data || screensResp.data || [];
        const pickingScreenIds: string[] = screens.map((s: any) => s.id || s.Id).filter(Boolean);

        if (pickingScreenIds.length === 0) {
          setPickerOptions(basePersonnel);
          setPickerInfo('Bu depoda Toplama ekranı bulunamadı. Tüm personel listelendi.');
          return;
        }

        const checks = await Promise.all(
          basePersonnel.map(async (personnel) => {
            const allowedScreens = await wmsPersonnelPermissionService.getMyWmsScreens(
              userDetails.companyId as string,
              personnel.id,
            );
            const hasAccess = allowedScreens.some((screenId) => pickingScreenIds.includes(screenId));
            return hasAccess ? personnel : null;
          }),
        );
        setPickerOptions(checks.filter((item): item is PersonnelOption => !!item));
      } catch {
        setPickerOptions([]);
        setPickerInfo('Personel listesi yüklenemedi.');
      } finally {
        setLoadingPickers(false);
      }
    };
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userDetails?.companyId, warehouseId]);

  const loadCarts = useCallback(async () => {
    if (!warehouseId) return;
    setLoadingCarts(true);
    try {
      const res = await equipmentService.getCarts(warehouseId);
      const data = res.data?.data || res.data || [];
      setCarts((Array.isArray(data) ? data : []).filter((c: Cart) => c.isActive));
    } catch {
      setCarts([]);
    }
    setLoadingCarts(false);
  }, [warehouseId]);

  useEffect(() => {
    if (open) loadCarts();
  }, [open, loadCarts]);

  const selectedPicker = useMemo(
    () => pickerOptions.find((p) => p.id === selectedPersonnelId) || null,
    [pickerOptions, selectedPersonnelId],
  );

  const selectedPickerName = selectedPicker?.name
    || (selectedPersonnelId && selectedPersonnelId === currentPickerId ? wave?.assignedPickerName : '')
    || '';

  const selectedCart = useMemo(
    () => carts.find((c) => c.id === selectedCartId) || null,
    [carts, selectedCartId],
  );

  const selectedCartCode = selectedCart?.cartCode
    || (selectedCartId && selectedCartId === currentCartId ? wave?.cartCode : '')
    || '';

  const assignPicker = useCallback(async () => {
    if (!wave || !selectedPicker) return;
    try {
      await waveService.assignPersonnelToWave({
        WaveId: wave.id,
        PersonnelId: selectedPicker.id,
        PersonnelName: selectedPicker.name,
      });
    } catch (err: any) {
      if (isLegacyFallbackStatus(err?.response?.status)) {
        await waveService.assignPicker({
          WaveId: wave.id,
          PickerId: selectedPicker.id,
          PickerName: selectedPicker.name,
        });
      } else {
        throw err;
      }
    }
  }, [wave, selectedPicker]);

  // Adımlar tamamen yerel ilerler; hiçbir atama bu aşamada backend'e yazılmaz.
  // Böylece "İptal" her şeyi atar, "Geri" ile seçim değiştirmek backend'i etkilemez
  // ve seçilen araba kilitlenmez. Tüm atamalar yalnızca "Başlat"ta kaydedilir.
  const handleNext = () => {
    setError('');
    if (activeStep === 0) {
      if (!selectedPersonnelId) { setError('Lütfen bir toplayıcı seçin.'); return; }
      setActiveStep(1);
      return;
    }
    if (activeStep === 1) {
      if (!selectedCartId) { setError('Lütfen bir araba seçin.'); return; }
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  // İptal/kapat: yerel seçimleri ve adımı sıfırla (hiçbir şey kaydedilmedi).
  const handleClose = () => {
    if (busy) return;
    setActiveStep(0);
    setSelectedPersonnelId('');
    setSelectedCartId('');
    setError('');
    onClose();
  };

  const handleStart = async () => {
    if (!wave) return;
    if (!selectedPersonnelId) { setError('Lütfen bir toplayıcı seçin.'); return; }
    if (!selectedCartId) { setError('Lütfen bir araba seçin.'); return; }
    setError('');
    setBusy(true);
    try {
      // Önce yalnızca değişen atamaları kaydet, sonra başlat.
      if (selectedPersonnelId !== currentPickerId) {
        await assignPicker();
      }
      if (selectedCartId !== currentCartId) {
        await waveService.assignCart({ WaveId: wave.id, CartId: selectedCartId });
      }
      await waveService.start(wave.id);
      window.dispatchEvent(new CustomEvent(WMS_WAVE_STATUS_EVENT, { detail: { warehouseId } }));
      localStorage.setItem(WMS_WAVE_STATUS_STORAGE_KEY, JSON.stringify({ ts: Date.now(), warehouseId }));
      onRefresh();
      setBusy(false);
      handleClose();
      return;
    } catch (err: any) {
      // Başlatma başarısız olursa kısmi atamalar kalmış olabilir; listeyi tazele ki
      // kullanıcı güncel durumu görsün.
      onRefresh();
      setError(getApiErrorMessage(err, 'İş emri başlatılamadı.'));
    }
    setBusy(false);
  };

  if (!wave) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        İş Emrini Hazırla
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {wave.waveNumber}
        </Typography>
        <IconButton
          aria-label="Kapat"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {/* Adım 1: Toplayıcı */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {pickerInfo && <Alert severity="warning">{pickerInfo}</Alert>}
            <Autocomplete
              options={pickerOptions}
              value={selectedPicker}
              loading={loadingPickers}
              getOptionLabel={(option) => option.name}
              onChange={(_e, value) => setSelectedPersonnelId(value?.id || '')}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Toplayıcı *"
                  placeholder="Personel seçin"
                  autoFocus
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPickers ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        {/* Adım 2: Araba */}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {loadingCarts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : carts.length === 0 ? (
              <Alert severity="info">Bu depoda uygun araba bulunamadı.</Alert>
            ) : (
              <RadioGroup value={selectedCartId} onChange={(e) => setSelectedCartId(e.target.value)}>
                {carts.map((cart) => {
                  const isAvailable = cart.status === 'Available';
                  const isCurrent = cart.id === currentCartId;
                  return (
                    <FormControlLabel
                      key={cart.id}
                      value={cart.id}
                      disabled={!isAvailable && !isCurrent}
                      control={<Radio size="small" />}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>{cart.cartCode}</Typography>
                          <Typography variant="body2" color="text.secondary">{cart.cartName}</Typography>
                          <Chip
                            label={isCurrent ? 'Mevcut' : (isAvailable ? 'Müsait' : cart.status)}
                            size="small"
                            variant="outlined"
                            color={isAvailable ? 'success' : isCurrent ? 'info' : 'default'}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                      )}
                    />
                  );
                })}
              </RadioGroup>
            )}
          </Box>
        )}

        {/* Adım 3: Özet & Başlat */}
        {activeStep === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Toplama başlatılmadan önce seçimleri kontrol edin.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonIcon color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">Toplayıcı</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedPickerName || 'Atanmamış'}</Typography>
              </Box>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ShoppingCartIcon color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">Araba</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedCartCode || 'Atanmamış'}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={activeStep === 0 || busy}
          color="inherit"
        >
          Geri
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} color="inherit" disabled={busy}>
            İptal
          </Button>
          {activeStep < 2 ? (
            <Button
              variant="contained"
              endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
              onClick={handleNext}
              disabled={busy}
            >
              İleri
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleStart}
              disabled={busy || !selectedPersonnelId || !selectedCartId}
            >
              Başlat
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WaveSetupWizard;
